/**
 * Microsoft Graph OAuth scope resolver.
 *
 * Maps per-service access modes (stored per connected account in the
 * connection's `microsoft/1` metadata) to
 * the exact set of Graph delegated scope URIs to request during authorization.
 *
 * This is the security-critical boundary: only scopes returned here are granted
 * to the runtime's access token, and Graph enforces them regardless of what the
 * agent attempts — including per-sub-request inside a $batch.
 *
 * Why there is no send "gate" (unlike Gmail): Microsoft exposes `Mail.Send` as a
 * distinct scope that `Mail.ReadWrite` does NOT include. So `write` mode
 * (Mail.ReadWrite) physically cannot send mail — Graph 403s sendMail /
 * messages/{id}/send / reply / replyAll / forward — and only `send` mode adds
 * Mail.Send. The dangerous boundary is the scope grant itself. Gmail had to use
 * a command parser because gmail.compose bundles drafts + send; Graph does not.
 *
 * See docs/microsoft-365-integration.md.
 */

// Graph delegated scope URIs. Requested as full resource URIs (unambiguous when
// mixed with OIDC scopes); the token endpoint may echo them either as full URIs
// or short names, which `diffScopes` canonicalizes.
// Reference: https://learn.microsoft.com/en-us/graph/permissions-reference
const GRAPH = "https://graph.microsoft.com/";

const MAIL_READ      = `${GRAPH}Mail.Read`;
const MAIL_READWRITE = `${GRAPH}Mail.ReadWrite`;
const MAIL_SEND      = `${GRAPH}Mail.Send`;

const CALENDARS_READ      = `${GRAPH}Calendars.Read`;
const CALENDARS_READWRITE = `${GRAPH}Calendars.ReadWrite`;

const CONTACTS_READ      = `${GRAPH}Contacts.Read`;
const CONTACTS_READWRITE = `${GRAPH}Contacts.ReadWrite`;

// Own-OneDrive scopes (not the tenant-wide .All variants) — see §10.
const FILES_READ      = `${GRAPH}Files.Read`;
const FILES_READWRITE = `${GRAPH}Files.ReadWrite`;

// SharePoint sites. Deliberately a service of its own rather than a third step
// on `files`, because the two cover different stores and merging them would make
// both labels lie: Sites.*.All reaches document libraries (so a OneDrive step
// would silently start meaning "and every site library"), while Files.*.All
// reaches the personal drive (so a SharePoint step would grant OneDrive to an
// account whose OneDrive step says disabled).
//
// A DELEGATED .All is not tenant-wide access: Graph intersects it with the
// signed-in user's own rights, so this reaches every site that user can already
// open and no more. What grows is token-leak amplification, which is why the
// service ships off by default and per connection.
// See docs/microsoft-sharepoint-access.md.
const SITES_READ = `${GRAPH}Sites.Read.All`;

// Reserved OIDC scopes (short names) plus User.Read, requested whenever at least
// one service is enabled. `offline_access` is what yields a refresh token (the
// Microsoft analog of Google's access_type=offline). `User.Read` backs the /me
// identity lookup. These are excluded from the drift diff (see DIFF_EXCLUDE):
// the token endpoint consumes openid/offline_access for the id_token and does
// not echo them, and may auto-add `profile`.
const ALWAYS_SCOPES = ["openid", "email", "offline_access", `${GRAPH}User.Read`] as const;

// --- Types ---

export type MailMode     = "disabled" | "read" | "write" | "send";
export type CalendarMode = "disabled" | "read" | "write";
export type ContactsMode = "disabled" | "read" | "write";
export type FilesMode    = "disabled" | "read" | "write";
/**
 * Read-only for now. `write` (Sites.ReadWrite.All) is the intended follow-on
 * once read is proven, and the DB CHECK constraint permits only these two in
 * the meantime — a constraint that allows a value nothing can produce would be
 * a lie about the schema.
 */
export type SharepointMode = "disabled" | "read";

export interface MicrosoftAccess {
  readonly mail:       MailMode;
  readonly calendar:   CalendarMode;
  readonly contacts:   ContactsMode;
  readonly files:      FilesMode;
  readonly sharepoint: SharepointMode;
}

// --- The vocabulary, as values ---
//
// One list per mode type, which is what the CHECK constraints on
// `microsoft_connections` used to repeat. On `integration_connections` the
// modes are keys of a jsonb document, and nothing at the schema level stops a
// value these lists do not know.

export const MAIL_MODES = ["disabled", "read", "write", "send"] as const satisfies readonly MailMode[];
export const READ_WRITE_MODES = ["disabled", "read", "write"] as const satisfies readonly CalendarMode[];
// Read-only until Sites.ReadWrite.All ships.
export const SHAREPOINT_MODES = ["disabled", "read"] as const satisfies readonly SharepointMode[];

const pick = <T extends string>(value: unknown, allowed: readonly T[]): T | null =>
  allowed.find((m) => m === value) ?? null;

/**
 * Validate an untrusted access payload: every field a mode that service
 * accepts, or null. For INPUT — the authorize and settings routes answer 400
 * on null, and on the authorize path the modes decide which scopes we ask
 * Microsoft for, so a value falling through to a default would request a scope
 * the user never picked.
 */
export const parseMicrosoftAccess = (input: unknown): MicrosoftAccess | null => {
  if (!input || typeof input !== "object") return null;
  const v: Record<string, unknown> = { ...input };
  const mail = pick(v.mail, MAIL_MODES);
  const calendar = pick(v.calendar, READ_WRITE_MODES);
  const contacts = pick(v.contacts, READ_WRITE_MODES);
  const files = pick(v.files, READ_WRITE_MODES);
  const sharepoint = pick(v.sharepoint, SHAREPOINT_MODES);
  if (!mail || !calendar || !contacts || !files || !sharepoint) return null;
  return { mail, calendar, contacts, files, sharepoint };
};

/**
 * Read STORED modes, each one it cannot recognise as `disabled`. For READERS
 * — a mode outside the vocabulary grants nothing, rather than failing the
 * whole connection or guessing generously.
 */
export const microsoftAccessOrDisabled = (input: unknown): MicrosoftAccess => {
  const v: Record<string, unknown> = input !== null && typeof input === "object" ? { ...input } : {};
  return {
    mail: pick(v.mail, MAIL_MODES) ?? "disabled",
    calendar: pick(v.calendar, READ_WRITE_MODES) ?? "disabled",
    contacts: pick(v.contacts, READ_WRITE_MODES) ?? "disabled",
    files: pick(v.files, READ_WRITE_MODES) ?? "disabled",
    sharepoint: pick(v.sharepoint, SHAREPOINT_MODES) ?? "disabled",
  };
};

// --- Per-service mappers (pure, table-driven) ---

const MAIL_SCOPES: Record<MailMode, readonly string[]> = {
  disabled: [],
  read:     [MAIL_READ],
  write:    [MAIL_READWRITE],
  send:     [MAIL_READWRITE, MAIL_SEND],
};

const CALENDAR_SCOPES: Record<CalendarMode, readonly string[]> = {
  disabled: [],
  read:     [CALENDARS_READ],
  write:    [CALENDARS_READWRITE],
};

const CONTACTS_SCOPES: Record<ContactsMode, readonly string[]> = {
  disabled: [],
  read:     [CONTACTS_READ],
  write:    [CONTACTS_READWRITE],
};

const FILES_SCOPES: Record<FilesMode, readonly string[]> = {
  disabled: [],
  read:     [FILES_READ],
  write:    [FILES_READWRITE],
};

const SHAREPOINT_SCOPES: Record<SharepointMode, readonly string[]> = {
  disabled: [],
  read:     [SITES_READ],
};

// --- Public API ---

/** True if every service is disabled — nothing to authorize. */
export const isEveryServiceDisabled = (access: MicrosoftAccess): boolean =>
  access.mail       === "disabled" &&
  access.calendar   === "disabled" &&
  access.contacts   === "disabled" &&
  access.files      === "disabled" &&
  access.sharepoint === "disabled";

/**
 * Resolve a MicrosoftAccess config to the full scope list to request.
 *
 * Returns an empty array when every service is disabled (no OAuth flow needed).
 * Otherwise includes the OIDC/identity scopes plus every service scope.
 * Deduplicated and sorted for stable comparison.
 */
export const resolveScopes = (access: MicrosoftAccess): readonly string[] => {
  if (isEveryServiceDisabled(access)) return [];

  const all = [
    ...ALWAYS_SCOPES,
    ...MAIL_SCOPES[access.mail],
    ...CALENDAR_SCOPES[access.calendar],
    ...CONTACTS_SCOPES[access.contacts],
    ...FILES_SCOPES[access.files],
    ...SHAREPOINT_SCOPES[access.sharepoint],
  ];

  return [...new Set(all)].sort();
};

// --- Organization approval ---
//
// A first version of this asserted that Sites.Read.All is the one scope here
// requiring a tenant administrator. That is WRONG, and measuring it is cheap:
//
//   az ad sp show --id 00000003-0000-0000-c000-000000000000 \
//     --query "oauth2PermissionScopes[?value=='Sites.Read.All'].type"
//
// returns "User" — the same as Mail.Read, Files.Read and Mail.Send. Entra
// classifies every scope this integration requests as user-consentable.
//
// What actually decides it is the TENANT'S user-consent policy, which we cannot
// see. Microsoft's default for modern tenants is "allow user consent for apps
// from verified publishers, for selected permissions (low impact)", and
// low-impact covers only openid / profile / email / offline_access / User.Read.
// So under the DEFAULT policy every data scope we ask for needs an
// administrator, and in a tenant that disables user consent outright — as the
// first enterprise customer's did — so does the very first mail connect.
//
// Hence the warning is not about SharePoint and not about a special list of
// scopes. It is about any non-trivial scope the connection does not already
// hold, because that is exactly the set that can stop the user with AADSTS65001
// on Microsoft's OWN sign-in page — before any redirect back, so our callback
// never runs, there is no log line and no error rate, and the connection simply
// never appears. We cannot detect it, so warning beforehand is the only
// mitigation available.

/**
 * Scopes Entra always lets a user consent to for themselves, per Microsoft's
 * "low impact" classification. Everything else is subject to tenant policy.
 */
const LOW_IMPACT_SCOPES: ReadonlySet<string> = new Set([
  "openid", "profile", "email", "offline_access", "User.Read",
]);

/**
 * Scopes `next` would request that this connection does not already hold and
 * that a restrictive tenant can refuse — i.e. what the user's organization may
 * have to approve before this save can work. Empty means nothing new is being
 * asked for.
 *
 * Compared against what Microsoft reported GRANTING rather than against the
 * previous modes, because the grant is the only evidence of what the tenant has
 * actually consented to: someone re-enabling a service they used to have, or
 * connecting where a colleague's consent already covers the scope, needs no
 * fresh approval. Canonicalized so the short-name form Entra echoes matches our
 * full-URI request.
 */
export const pendingApprovalScopes = (
  next: MicrosoftAccess,
  grantedScopes: readonly string[],
): readonly string[] => {
  const held = new Set([...grantedScopes].map(stripGraphPrefix));
  return resolveScopes(next)
    .map(stripGraphPrefix)
    .filter((s) => !LOW_IMPACT_SCOPES.has(s) && !held.has(s));
};

/**
 * Parse the `scope` string of a token response into the stored
 * `granted_scopes` shape.
 *
 * Shared because TWO writers persist that column and a disagreement between
 * them reads as drift: the OAuth callback on the initial code exchange, and the
 * runtime's refresh path (which is the only place a *narrowing* — an admin
 * revoking one permission from the enterprise application — is ever reported).
 *
 * Returns [] for a missing or blank value. Callers must treat that as "no
 * answer" and leave the stored value alone, never as "nothing is granted".
 */
export const parseGrantedScopes = (scope: string | undefined | null): readonly string[] =>
  (scope ?? "").split(" ").filter(Boolean).sort();

// --- Scope diff (drives the Settings `drift` badge ONLY) ---
//
// IMPORTANT: this is display-only. The revoke/re-auth DECISION compares
// intent-to-intent (resolveScopes(next) vs resolveScopes(current)), never
// granted-vs-intent — mirroring google/.../access route, which does this
// deliberately to stay immune to scope-name normalization. A normalization bug
// here can only produce a misleading badge, never a spurious revoke.
//
// Canonicalization handles the two ways Graph's echoed grant differs from our
// requested intent:
//   1. resource prefix — token may echo `Mail.Read` or the full URI form.
//   2. identity scopes — openid/email/profile/offline_access/User.Read are
//      consumed/auto-added and not a meaningful part of the user's selection.

export type ScopeDiff = "match" | "widen" | "narrow" | "drift";

const stripGraphPrefix = (scope: string): string =>
  scope.startsWith(GRAPH) ? scope.slice(GRAPH.length) : scope;

// Excluded from the diff entirely (compared on user-selected service scopes).
const DIFF_EXCLUDE: ReadonlySet<string> = new Set([
  "openid", "email", "profile", "offline_access", "User.Read",
]);

const canonicalScopes = (scopes: readonly string[]): Set<string> =>
  new Set(scopes.map(stripGraphPrefix).filter((s) => !DIFF_EXCLUDE.has(s)));

/**
 * What saving a change to one connection's access modes does to its grant.
 *
 * ONE definition, because two consumers act on it and they must not be able to
 * disagree: the settings panel warns "saving this disconnects the account", and
 * the PATCH route actually deletes the connection. A hand-mirrored copy of this
 * rule means a future verdict from `diffScopes` either deletes a live grant
 * with no warning shown, or shows a red warning for a save that does nothing.
 *
 *   "noop"   — same scopes; save in place.
 *   "widen"  — needs scopes the stored grant lacks; re-authorization.
 *   "revoke" — takes scopes away. Microsoft has already issued the grant and
 *              cannot narrow it, so the only way to honour the user's intent is
 *              to discard it and have them connect again.
 *
 * Compares intent to intent (mode set vs mode set), never against the scopes
 * Microsoft reported granting — it returns short names and adds its own
 * (profile, openid, offline_access), which read as drift on a clean widen.
 */
export type AccessChange = "noop" | "widen" | "revoke";

export const classifyAccessChange = (
  current: MicrosoftAccess,
  next: MicrosoftAccess,
): AccessChange => {
  const diff = diffScopes(resolveScopes(next), resolveScopes(current));
  if (diff === "match") return "noop";
  if (diff === "widen") return "widen";
  return "revoke"; // "narrow" and "drift" both drop something the grant holds
};

/**
 * Compare intent (user settings) against granted (snapshot from token).
 * "widen" — intent needs MORE than granted; "narrow" — LESS; "match" —
 * identical (after canonicalization); "drift" — neither is a subset.
 */
export const diffScopes = (
  intent: readonly string[],
  granted: readonly string[],
): ScopeDiff => {
  const intentSet = canonicalScopes(intent);
  const grantedSet = canonicalScopes(granted);

  const intentSubsetOfGranted = [...intentSet].every((s) => grantedSet.has(s));
  const grantedSubsetOfIntent = [...grantedSet].every((s) => intentSet.has(s));

  if (intentSubsetOfGranted && grantedSubsetOfIntent) return "match";
  if (intentSubsetOfGranted) return "narrow";   // granted has extra
  if (grantedSubsetOfIntent) return "widen";    // intent has extra
  return "drift";                                // neither way is a subset
};
